import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import { AutoPricingInputs } from 'src/WebApiController';

export const useAutoPricingInputFormHasChanges = () => {
  const { watch, formState } = useFormContext<AutoPricingInputs>();

  const input = watch();
  const defaultValues = formState.defaultValues;

  const hasChangesInCompSettings = useMemo(() => {
    return (
      input.compListingFloor !== defaultValues?.compListingFloor ||
      input.compListingCeiling !== defaultValues?.compListingCeiling ||
      input.compListingMode !== defaultValues?.compListingMode ||
      input.compListingOnlyForSameZoneEnabled !==
        defaultValues?.compListingOnlyForSameZoneEnabled ||
      input.compListingOnlyForSelectedSectionsEnabled !==
        defaultValues?.compListingOnlyForSelectedSectionsEnabled ||
      input.compListingExcludeFanInventory !==
        defaultValues?.compListingExcludeFanInventory ||
      input.compListingExcludeDefects !==
        defaultValues?.compListingExcludeDefects ||
      input.compListingIncludeUnmappedInventory !==
        defaultValues?.compListingIncludeUnmappedInventory ||
      input.compListingQuantityScoreAdjustmentEnabled !==
        defaultValues?.compListingQuantityScoreAdjustmentEnabled ||
      input.compListingQuantityScoreAdjustmentOverrideJson !==
        defaultValues?.compListingQuantityScoreAdjustmentOverrideJson ||
      !isEqual(
        input.compListingSelectedSectionSettings,
        defaultValues?.compListingSelectedSectionSettings
      ) ||
      !isEqual(
        input.compListingQuantityFilters,
        defaultValues?.compListingQuantityFilters
      )
    );
  }, [
    defaultValues?.compListingCeiling,
    defaultValues?.compListingExcludeDefects,
    defaultValues?.compListingExcludeFanInventory,
    defaultValues?.compListingIncludeUnmappedInventory,
    defaultValues?.compListingFloor,
    defaultValues?.compListingMode,
    defaultValues?.compListingOnlyForSameZoneEnabled,
    defaultValues?.compListingOnlyForSelectedSectionsEnabled,
    defaultValues?.compListingQuantityFilters,
    defaultValues?.compListingQuantityScoreAdjustmentEnabled,
    defaultValues?.compListingQuantityScoreAdjustmentOverrideJson,
    defaultValues?.compListingSelectedSectionSettings,
    input.compListingCeiling,
    input.compListingExcludeDefects,
    input.compListingExcludeFanInventory,
    input.compListingIncludeUnmappedInventory,
    input.compListingFloor,
    input.compListingMode,
    input.compListingOnlyForSameZoneEnabled,
    input.compListingOnlyForSelectedSectionsEnabled,
    input.compListingQuantityFilters,
    input.compListingQuantityScoreAdjustmentEnabled,
    input.compListingQuantityScoreAdjustmentOverrideJson,
    input.compListingSelectedSectionSettings,
  ]);

  const hasChangesInUndercutSettings = useMemo(() => {
    return (
      input.undercutMode !== defaultValues?.undercutMode ||
      input.undercutAbsoluteAmount !== defaultValues?.undercutAbsoluteAmount ||
      input.undercutRelativeAmount !== defaultValues?.undercutRelativeAmount ||
      input.undercutReferenceListingPosition !==
        defaultValues?.undercutReferenceListingPosition
    );
  }, [
    defaultValues?.undercutAbsoluteAmount,
    defaultValues?.undercutMode,
    defaultValues?.undercutReferenceListingPosition,
    defaultValues?.undercutRelativeAmount,
    input.undercutAbsoluteAmount,
    input.undercutMode,
    input.undercutReferenceListingPosition,
    input.undercutRelativeAmount,
  ]);

  const hasChangesInOutlierSettings = useMemo(() => {
    return (
      input.outlierMode !== defaultValues?.outlierMode ||
      input.outlierStandardDeviations !==
        defaultValues?.outlierStandardDeviations ||
      input.outlierKthLowestLimit !== defaultValues?.outlierKthLowestLimit ||
      input.outlierKthLowestLimitRelativeSpacing !==
        defaultValues?.outlierKthLowestLimitRelativeSpacing ||
      input.outlierKthLowestLimitAbsoluteSpacing !==
        defaultValues?.outlierKthLowestLimitAbsoluteSpacing
    );
  }, [
    defaultValues?.outlierKthLowestLimit,
    defaultValues?.outlierKthLowestLimitAbsoluteSpacing,
    defaultValues?.outlierKthLowestLimitRelativeSpacing,
    defaultValues?.outlierMode,
    defaultValues?.outlierStandardDeviations,
    input.outlierKthLowestLimit,
    input.outlierKthLowestLimitAbsoluteSpacing,
    input.outlierKthLowestLimitRelativeSpacing,
    input.outlierMode,
    input.outlierStandardDeviations,
  ]);

  return {
    hasChangesInCompSettings,
    hasChangesInUndercutSettings,
    hasChangesInOutlierSettings,
    hasAnyPricingSettingChanges:
      hasChangesInCompSettings ||
      hasChangesInUndercutSettings ||
      hasChangesInOutlierSettings,
  };
};
